import * as jotai from 'jotai'
import * as React from 'react'

import * as api from '@owl-nest/api-client/latest'
import * as entities from '@owl-nest/entities'
import * as logger from '@owl-nest/logger'
import * as services from '@owl-nest/services'
import * as shopify from '@owl-nest/shopify'
import * as entity from '@boutique/entities'
import * as cold from '@owl-nest/cold-data/client'
import * as hooks from '@owl-nest/hooks'
import { Either } from '@owl-nest/monad'

const REVIEWS_FETCH_SIZE = 9

export async function getProduct(
  shopifyProductId: number,
  reviewParams: URLSearchParams = new URLSearchParams(),
): Promise<api.ApiResponse<entity.product.Product | null>> {
  const productId = shopify.utils.toGID(String(shopifyProductId))
  const result = await shopify.product.get(productId)

  return result.nextAsync(async (shopifyProduct) => {
    if (
      shopifyProduct === undefined ||
      shopifyProduct.tags.includes('offline') ||
      !shopifyProduct.tags.includes('online')
    ) {
      return null
    }

    if (!reviewParams.has('limit')) {
      reviewParams.set('limit', String(REVIEWS_FETCH_SIZE))
    }

    const reviews = await getReviews(String(shopifyProductId), reviewParams)

    return entity.product.product(String(shopifyProductId), shopifyProduct, reviews, cold)
  })
}

type UseProductConfig = {
  id: number
  fetchOnMount?: boolean | 'force'
}

export const PRODUCT_CACHE = jotai.atom<hooks.Cache<entity.product.Product>>({})

export function useProduct({ id, fetchOnMount = false }: UseProductConfig) {
  const sharedQuery = hooks.useSharedQuery(
    async (id: number) => {
      return (await getProduct(id)).next((result) => {
        if (result === null) {
          return Either.left<api.RequestFailure, entity.product.Product>({
            status: -1,
            url: 'shopify',
            body: undefined,
          })
        }
        return Either.right<api.RequestFailure, entity.product.Product>(result)
      })
    },
    {
      atom: PRODUCT_CACHE,
      id: useProductId,
    },
  )

  React.useEffect(() => {
    if (fetchOnMount === 'force' && getResponse().status !== hooks.QueryStatus.PENDING) {
      query()
    } else if (fetchOnMount && getResponse().status === hooks.QueryStatus.PRISTINE) {
      query()
    }
  }, [useProductId(id)])

  return {
    query,
    response: getResponse(),
  }

  function getResponse(): hooks.Response<entity.product.Product> {
    return sharedQuery.getCurrentResponse([id])
  }

  function query() {
    return sharedQuery.query(id)
  }
}

export function useProductId(id: number) {
  return JSON.stringify([id])
}

export async function getReviews(
  shopifyProductId: string,
  params: URLSearchParams,
): Promise<api.Pageable<api.ProductReviewPage> | void> {
  const reviewsResponse = await api.api.get.productReview({
    urlParams: {
      productId: shopifyProductId,
    },
    getParams: params,
  })

  return reviewsResponse.caseOf({
    left: (failure) => {
      logger.err(`[product] Unable to get review rewards - Code ${failure.status}`, {
        productId: shopifyProductId,
        getParams: params.toString(),
        failure,
      })
    },
    right: (success) => {
      return success.body
    },
  })
}

const STAR_PRODUCT_CACHE = jotai.atom<hooks.Cache<entities.product.MediumProduct[]>>({})

type UseStarProductConfig = {
  vendor?: string
  fetchOnMount?: boolean | 'force'
}

export function useStarProduct({ vendor, fetchOnMount = false }: UseStarProductConfig) {
  const sharedQuery = hooks.useSharedQuery(
    (vendor: string) =>
      services.product.getMediumProductsWithDescription({
        first: 4,
        query: `(tag:star OR tag:Star) AND vendor:${vendor}`,
      }),
    {
      atom: STAR_PRODUCT_CACHE,
      id: starProductId,
    },
  )

  React.useEffect(() => {
    if (fetchOnMount === 'force' && getResponse().status !== hooks.QueryStatus.PENDING) {
      query()
    } else if (fetchOnMount && getResponse().status === hooks.QueryStatus.PRISTINE) {
      query()
    }
  }, [starProductId(vendor)])

  return {
    query,
    response: getResponse(),
  }

  function getResponse(): hooks.Response<entities.product.MediumProduct[]> {
    if (vendor === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    return sharedQuery.getCurrentResponse([vendor])
  }

  function query() {
    if (vendor !== undefined) {
      return sharedQuery.query(vendor)
    }
  }
}

function starProductId(vendor?: string) {
  return JSON.stringify([vendor])
}
